import { DialogComponent } from '@theme/dialog';

/**
 * A custom element for the USP grid drawer.
 *
 * Each USP card ships its accordion row in a <template>. The rows are copied
 * into the drawer every time it opens, so edits in the theme editor are always
 * reflected, and the clicked USP's row is the one that opens.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The drawer dialog.
 * @property {HTMLElement} rows - The drawer's accordion container.
 *
 * @extends {DialogComponent}
 */
class UspGridComponent extends DialogComponent {
  requiredRefs = ['dialog', 'rows'];

  /**
   * Opens the drawer with the clicked USP's accordion row expanded.
   *
   * `event.target` is the element carrying the `on:click` attribute, see
   * the note in size-guide.js.
   *
   * @param {Event} event - The click event.
   */
  openItem = (event) => {
    const target = event.target;
    const uspId = target instanceof HTMLElement ? target.dataset.uspId : undefined;

    this.#renderRows(uspId);
    this.showDialog();
  };

  /**
   * @param {string | undefined} openId - The USP whose row should be open.
   */
  #renderRows(openId) {
    const { rows } = this.refs;
    rows.replaceChildren();

    for (const template of this.querySelectorAll('template.usp-grid__row-template')) {
      if (!(template instanceof HTMLTemplateElement)) continue;

      const row = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const accordion = row.querySelector('accordion-custom');

      // accordion-custom sets `details.open` from these attributes when it connects
      if (accordion && template.dataset.uspId === openId) {
        accordion.setAttribute('open-by-default-on-mobile', '');
        accordion.setAttribute('open-by-default-on-desktop', '');
      }

      rows.append(row);
    }
  }
}

if (!customElements.get('usp-grid-component')) {
  customElements.define('usp-grid-component', UspGridComponent);
}
